import { useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Plus, Eye, EyeOff, Lock, Pencil, Camera, X, Image as ImageIcon, Upload } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [films, setFilms] = useState([]);
  const [signups, setSignups] = useState([]);
  const [newFilm, setNewFilm] = useState({ title: "", video_url: "", poster_url: "", synopsis: "" });
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({ synopsis: "", poster_url: "", t: "0" });
  const [capturing, setCapturing] = useState(false);

  // Image library
  const [images, setImages] = useState([]);
  const [newImg, setNewImg] = useState({ title: "", caption: "", alt: "", date: "", linkedin_url: "" });
  const [newImgFile, setNewImgFile] = useState(null);
  const [savingImg, setSavingImg] = useState(false);
  const [editImgId, setEditImgId] = useState(null);
  const [imgDraft, setImgDraft] = useState({ title: "", caption: "", alt: "", date: "", linkedin_url: "" });

  const loadAll = async (pc) => {
    const [fRes, nRes, iRes] = await Promise.all([
      fetch(`${API}/api/admin/films?passcode=${encodeURIComponent(pc)}`),
      fetch(`${API}/api/admin/newsletter?passcode=${encodeURIComponent(pc)}`),
      fetch(`${API}/api/admin/library?passcode=${encodeURIComponent(pc)}`),
    ]);
    if (!fRes.ok) throw new Error("bad passcode");
    setFilms(await fRes.json());
    setSignups(nRes.ok ? await nRes.json() : []);
    setImages(iRes.ok ? await iRes.json() : []);
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      await loadAll(passcode);
      setAuthed(true);
    } catch (err) {
      toast.error("Invalid passcode");
    }
  };

  const updateFilm = async (id, body) => {
    await fetch(`${API}/api/admin/films/${id}?passcode=${encodeURIComponent(passcode)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    await loadAll(passcode);
  };

  const openEdit = (f) => {
    if (editId === f.id) {
      setEditId(null);
      return;
    }
    setEditId(f.id);
    setDraft({ synopsis: f.synopsis || "", poster_url: f.poster_url || "", t: "0" });
  };

  const saveEdit = async (f) => {
    await updateFilm(f.id, { synopsis: draft.synopsis, poster_url: draft.poster_url });
    toast.success("Saved");
    setEditId(null);
  };

  const captureFrame = async (f) => {
    const secs = parseFloat(draft.t) || 0;
    setCapturing(true);
    try {
      const res = await fetch(
        `${API}/api/admin/films/${f.id}/capture-poster?t=${secs}&passcode=${encodeURIComponent(passcode)}`,
        { method: "POST" }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "failed");
      }
      const updated = await res.json();
      setDraft((d) => ({ ...d, poster_url: updated.poster_url }));
      await loadAll(passcode);
      toast.success("Frame captured as thumbnail");
    } catch (e) {
      toast.error(e.message || "Could not capture frame");
    } finally {
      setCapturing(false);
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= films.length) return;
    const a = films[index];
    const b = films[target];
    await Promise.all([
      fetch(`${API}/api/admin/films/${a.id}?passcode=${encodeURIComponent(passcode)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: b.order }),
      }),
      fetch(`${API}/api/admin/films/${b.id}?passcode=${encodeURIComponent(passcode)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: a.order }),
      }),
    ]);
    await loadAll(passcode);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this film?")) return;
    await fetch(`${API}/api/admin/films/${id}?passcode=${encodeURIComponent(passcode)}`, {
      method: "DELETE",
    });
    await loadAll(passcode);
  };

  const addFilm = async (e) => {
    e.preventDefault();
    if (!newFilm.title || !newFilm.video_url) {
      toast.error("Title and video URL are required");
      return;
    }
    const res = await fetch(`${API}/api/admin/films?passcode=${encodeURIComponent(passcode)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newFilm),
    });
    if (res.ok) {
      toast.success("Film added");
      setNewFilm({ title: "", video_url: "", poster_url: "", synopsis: "" });
      await loadAll(passcode);
    } else {
      toast.error("Could not add film");
    }
  };

  // ----- Image library -----
  const fileUrl = (im) => `${API}/api/library/file/${im.filename}`;

  const updateImage = async (id, body) => {
    await fetch(`${API}/api/admin/library/${id}?passcode=${encodeURIComponent(passcode)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    await loadAll(passcode);
  };

  const openImgEdit = (im) => {
    if (editImgId === im.id) {
      setEditImgId(null);
      return;
    }
    setEditImgId(im.id);
    setImgDraft({
      title: im.title || "",
      caption: im.caption || "",
      alt: im.alt || "",
      date: im.date || "",
      linkedin_url: im.linkedin_url || "",
    });
  };

  const saveImgEdit = async (im) => {
    await updateImage(im.id, imgDraft);
    toast.success("Saved");
    setEditImgId(null);
  };

  const replaceImage = async (im, fileObj) => {
    if (!fileObj) return;
    const fd = new FormData();
    fd.append("file", fileObj);
    const res = await fetch(`${API}/api/admin/library/${im.id}/image?passcode=${encodeURIComponent(passcode)}`, {
      method: "POST",
      body: fd,
    });
    if (res.ok) {
      toast.success("Image replaced");
      await loadAll(passcode);
    } else {
      toast.error("Could not replace image");
    }
  };

  const moveImage = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= images.length) return;
    const a = images[index];
    const b = images[target];
    await Promise.all([
      fetch(`${API}/api/admin/library/${a.id}?passcode=${encodeURIComponent(passcode)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: b.order }),
      }),
      fetch(`${API}/api/admin/library/${b.id}?passcode=${encodeURIComponent(passcode)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: a.order }),
      }),
    ]);
    await loadAll(passcode);
  };

  const removeImage = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    await fetch(`${API}/api/admin/library/${id}?passcode=${encodeURIComponent(passcode)}`, { method: "DELETE" });
    await loadAll(passcode);
  };

  const addImage = async (e) => {
    e.preventDefault();
    if (!newImgFile || !newImg.title) {
      toast.error("An image file and title are required");
      return;
    }
    setSavingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", newImgFile);
      fd.append("title", newImg.title);
      fd.append("caption", newImg.caption);
      fd.append("alt", newImg.alt);
      fd.append("date", newImg.date);
      fd.append("linkedin_url", newImg.linkedin_url);
      const res = await fetch(`${API}/api/admin/library?passcode=${encodeURIComponent(passcode)}`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) throw new Error();
      toast.success("Image added");
      setNewImg({ title: "", caption: "", alt: "", date: "", linkedin_url: "" });
      setNewImgFile(null);
      await loadAll(passcode);
    } catch (err) {
      toast.error("Could not add image");
    } finally {
      setSavingImg(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center bg-ivory px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm bg-white border border-ink/10 p-8 shadow-lg">
          <span className="w-12 h-12 grid place-items-center rounded-full bg-windsor text-ivory">
            <Lock size={20} />
          </span>
          <h1 className="font-serif text-3xl text-ink mt-5">Films admin</h1>
          <p className="text-ink/60 font-light mt-2 text-sm">Enter your passcode to manage films.</p>
          <input
            type="password"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Passcode"
            data-testid="admin-passcode-input"
            className="w-full mt-6 border border-ink/20 px-4 py-3 focus:outline-none focus:border-windsor"
          />
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="w-full mt-4 bg-[#C2410C] text-ivory py-3 font-medium hover:bg-windsor-dark transition-colors"
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory py-16" data-testid="admin-dashboard">
      <div className="container-px max-w-4xl">
        <h1 className="font-serif text-4xl text-ink">Films</h1>
        <p className="text-ink/60 font-light mt-1">
          Reorder, rename, hide, or add films for the On Tough Trust page.
        </p>

        <div className="mt-8 space-y-4">
          {films.map((f, i) => (
            <div key={f.id} data-testid={`admin-film-${i}`} className="bg-white border border-ink/10">
              <div className="p-5 flex items-center gap-4">
                <div className="flex flex-col gap-1">
                  <button onClick={() => move(i, -1)} data-testid={`admin-up-${i}`} className="text-ink/50 hover:text-windsor disabled:opacity-30" disabled={i === 0}>
                    <ArrowUp size={16} />
                  </button>
                  <button onClick={() => move(i, 1)} data-testid={`admin-down-${i}`} className="text-ink/50 hover:text-windsor disabled:opacity-30" disabled={i === films.length - 1}>
                    <ArrowDown size={16} />
                  </button>
                </div>
                <img src={`${f.poster_url}`} alt="" className="w-24 h-14 object-cover border border-ink/10 bg-black shrink-0" />
                <input
                  defaultValue={f.title}
                  key={f.title}
                  data-testid={`admin-title-${i}`}
                  onBlur={(e) => e.target.value !== f.title && updateFilm(f.id, { title: e.target.value })}
                  className="flex-1 border border-transparent hover:border-ink/15 focus:border-windsor px-2 py-1.5 text-ink font-serif text-lg focus:outline-none"
                />
                <span className="text-ink/50 text-sm tabular-nums w-16 text-right">{f.views} views</span>
                <button onClick={() => openEdit(f)} data-testid={`admin-edit-${i}`} className={`p-2 ${editId === f.id ? "text-windsor" : "text-ink/40 hover:text-windsor"}`} title="Edit">
                  {editId === f.id ? <X size={18} /> : <Pencil size={18} />}
                </button>
                <button
                  onClick={() => updateFilm(f.id, { enabled: !f.enabled })}
                  data-testid={`admin-toggle-${i}`}
                  className={`p-2 ${f.enabled ? "text-windsor" : "text-ink/30"}`}
                  title={f.enabled ? "Visible" : "Hidden"}
                >
                  {f.enabled ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
                <button onClick={() => remove(f.id)} data-testid={`admin-delete-${i}`} className="p-2 text-ink/40 hover:text-red-600">
                  <Trash2 size={18} />
                </button>
              </div>

              {editId === f.id && (
                <div className="border-t border-ink/10 p-5 bg-ivory/60 space-y-5" data-testid={`admin-edit-panel-${i}`}>
                  <div>
                    <label className="text-sm font-medium text-ink/70">Synopsis</label>
                    <textarea
                      value={draft.synopsis}
                      onChange={(e) => setDraft({ ...draft, synopsis: e.target.value })}
                      rows={3}
                      data-testid={`admin-edit-synopsis-${i}`}
                      className="w-full mt-1 border border-ink/20 px-3 py-2 focus:outline-none focus:border-windsor resize-y"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-ink/70">Thumbnail freeze-frame</label>
                    <div className="flex flex-wrap items-center gap-3 mt-1">
                      <img src={`${f.poster_url}`} alt="" className="w-40 aspect-video object-cover border border-ink/10 bg-black" />
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-ink/60">at</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={draft.t}
                          onChange={(e) => setDraft({ ...draft, t: e.target.value })}
                          data-testid={`admin-capture-time-${i}`}
                          className="w-20 border border-ink/20 px-2 py-1.5 focus:outline-none focus:border-windsor"
                        />
                        <span className="text-sm text-ink/60">sec</span>
                        <button
                          onClick={() => captureFrame(f)}
                          disabled={capturing}
                          data-testid={`admin-capture-btn-${i}`}
                          className="inline-flex items-center gap-2 bg-ink text-ivory px-4 py-2 text-sm font-medium hover:bg-ink/85 transition-colors disabled:opacity-60"
                        >
                          <Camera size={15} /> {capturing ? "Capturing…" : "Grab frame"}
                        </button>
                      </div>
                    </div>
                    <input
                      value={draft.poster_url}
                      onChange={(e) => setDraft({ ...draft, poster_url: e.target.value })}
                      placeholder="…or paste a poster image URL"
                      data-testid={`admin-edit-poster-${i}`}
                      className="w-full mt-3 border border-ink/20 px-3 py-2 text-sm focus:outline-none focus:border-windsor"
                    />
                  </div>
                  <button
                    onClick={() => saveEdit(f)}
                    data-testid={`admin-edit-save-${i}`}
                    className="bg-[#C2410C] text-ivory px-6 py-2.5 font-medium hover:bg-windsor-dark transition-colors"
                  >
                    Save changes
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ADD FILM */}
        <form onSubmit={addFilm} className="mt-10 bg-white border border-ink/10 p-6" data-testid="admin-add-form">
          <h2 className="font-serif text-2xl text-ink flex items-center gap-2">
            <Plus size={20} /> Add a film
          </h2>
          <p className="text-ink/60 text-sm font-light mt-1">
            Paste the video and poster URLs (an uploaded path like <code>/video-new.mp4</code> or a full https link).
          </p>
          <div className="grid gap-3 mt-4">
            <input
              value={newFilm.title}
              onChange={(e) => setNewFilm({ ...newFilm, title: e.target.value })}
              placeholder="Title"
              data-testid="admin-new-title"
              className="border border-ink/20 px-4 py-2.5 focus:outline-none focus:border-windsor"
            />
            <input
              value={newFilm.video_url}
              onChange={(e) => setNewFilm({ ...newFilm, video_url: e.target.value })}
              placeholder="Video URL (/video-xyz.mp4 or https://…)"
              data-testid="admin-new-video"
              className="border border-ink/20 px-4 py-2.5 focus:outline-none focus:border-windsor"
            />
            <input
              value={newFilm.poster_url}
              onChange={(e) => setNewFilm({ ...newFilm, poster_url: e.target.value })}
              placeholder="Poster image URL (optional)"
              data-testid="admin-new-poster"
              className="border border-ink/20 px-4 py-2.5 focus:outline-none focus:border-windsor"
            />
            <textarea
              value={newFilm.synopsis}
              onChange={(e) => setNewFilm({ ...newFilm, synopsis: e.target.value })}
              placeholder="Short synopsis shown under the video (optional)"
              rows={3}
              data-testid="admin-new-synopsis"
              className="border border-ink/20 px-4 py-2.5 focus:outline-none focus:border-windsor resize-y"
            />
            <button type="submit" data-testid="admin-add-btn" className="bg-[#C2410C] text-ivory py-3 font-medium hover:bg-windsor-dark transition-colors">
              Add film
            </button>
          </div>
        </form>

        {/* IMAGE LIBRARY */}
        <div className="mt-16" data-testid="admin-library">
          <h2 className="font-serif text-3xl text-ink flex items-center gap-2">
            <ImageIcon size={24} /> Images
          </h2>
          <p className="text-ink/60 font-light mt-1">
            The Tough Trust library shown below the films. Reorder, edit or delete entries — newest by date shows first on the page.
          </p>

          <div className="mt-6 space-y-4">
            {images.length === 0 && (
              <p className="text-ink/50 font-light bg-white border border-ink/10 p-5" data-testid="admin-library-empty">
                No images yet. Add your first below.
              </p>
            )}
            {images.map((im, i) => (
              <div key={im.id} data-testid={`admin-image-${i}`} className="bg-white border border-ink/10">
                <div className="p-5 flex items-center gap-4">
                  <div className="flex flex-col gap-1">
                    <button onClick={() => moveImage(i, -1)} data-testid={`admin-image-up-${i}`} className="text-ink/50 hover:text-windsor disabled:opacity-30" disabled={i === 0}>
                      <ArrowUp size={16} />
                    </button>
                    <button onClick={() => moveImage(i, 1)} data-testid={`admin-image-down-${i}`} className="text-ink/50 hover:text-windsor disabled:opacity-30" disabled={i === images.length - 1}>
                      <ArrowDown size={16} />
                    </button>
                  </div>
                  <img src={fileUrl(im)} alt={im.alt || ""} className="w-24 h-16 object-cover border border-ink/10 bg-cloud shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg text-ink truncate">{im.title}</p>
                    <p className="text-ink/40 text-xs">{im.date || "no date"} · {im.width}×{im.height}{im.linkedin_url ? " · LinkedIn" : ""}</p>
                  </div>
                  <button onClick={() => openImgEdit(im)} data-testid={`admin-image-edit-${i}`} className={`p-2 ${editImgId === im.id ? "text-windsor" : "text-ink/40 hover:text-windsor"}`} title="Edit">
                    {editImgId === im.id ? <X size={18} /> : <Pencil size={18} />}
                  </button>
                  <button onClick={() => removeImage(im.id)} data-testid={`admin-image-delete-${i}`} className="p-2 text-ink/40 hover:text-red-600">
                    <Trash2 size={18} />
                  </button>
                </div>

                {editImgId === im.id && (
                  <div className="border-t border-ink/10 p-5 bg-ivory/60 space-y-4" data-testid={`admin-image-edit-panel-${i}`}>
                    <div>
                      <label className="text-sm font-medium text-ink/70">Title</label>
                      <input
                        value={imgDraft.title}
                        onChange={(e) => setImgDraft({ ...imgDraft, title: e.target.value })}
                        data-testid={`admin-image-edit-title-${i}`}
                        className="w-full mt-1 border border-ink/20 px-3 py-2 focus:outline-none focus:border-windsor"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-ink/70">Caption (blank line between paragraphs)</label>
                      <textarea
                        value={imgDraft.caption}
                        onChange={(e) => setImgDraft({ ...imgDraft, caption: e.target.value })}
                        rows={4}
                        data-testid={`admin-image-edit-caption-${i}`}
                        className="w-full mt-1 border border-ink/20 px-3 py-2 focus:outline-none focus:border-windsor resize-y"
                      />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-ink/70">Alt text</label>
                        <input
                          value={imgDraft.alt}
                          onChange={(e) => setImgDraft({ ...imgDraft, alt: e.target.value })}
                          data-testid={`admin-image-edit-alt-${i}`}
                          className="w-full mt-1 border border-ink/20 px-3 py-2 focus:outline-none focus:border-windsor"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-ink/70">Date</label>
                        <input
                          type="date"
                          value={imgDraft.date}
                          onChange={(e) => setImgDraft({ ...imgDraft, date: e.target.value })}
                          data-testid={`admin-image-edit-date-${i}`}
                          className="w-full mt-1 border border-ink/20 px-3 py-2 focus:outline-none focus:border-windsor"
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={() => saveImgEdit(im)}
                        data-testid={`admin-image-save-${i}`}
                        className="bg-[#C2410C] text-ivory px-6 py-2.5 font-medium hover:bg-windsor-dark transition-colors"
                      >
                        Save changes
                      </button>
                      <label className="inline-flex items-center gap-2 bg-ink text-ivory px-4 py-2.5 text-sm font-medium hover:bg-ink/85 transition-colors cursor-pointer" data-testid={`admin-image-replace-label-${i}`}>
                        <Upload size={15} /> Replace image
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => replaceImage(im, e.target.files?.[0])} data-testid={`admin-image-replace-${i}`} />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ADD IMAGE */}
          <form onSubmit={addImage} className="mt-8 bg-white border border-ink/10 p-6" data-testid="admin-add-image-form">
            <h3 className="font-serif text-2xl text-ink flex items-center gap-2">
              <Plus size={20} /> Add an image
            </h3>
            <div className="grid gap-3 mt-4">
              <label className="inline-flex items-center gap-2 border border-dashed border-ink/25 px-4 py-3 cursor-pointer hover:border-windsor transition-colors" data-testid="admin-new-image-label">
                <Upload size={16} className="text-ink/50" />
                <span className="text-ink/70 text-sm">{newImgFile ? newImgFile.name : "Choose an image to upload"}</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setNewImgFile(e.target.files?.[0] || null)}
                  data-testid="admin-new-image-file"
                />
              </label>
              <input
                value={newImg.title}
                onChange={(e) => setNewImg({ ...newImg, title: e.target.value })}
                placeholder="Title (used for the file name, e.g. Truth comes first)"
                data-testid="admin-new-image-title"
                className="border border-ink/20 px-4 py-2.5 focus:outline-none focus:border-windsor"
              />
              <textarea
                value={newImg.caption}
                onChange={(e) => setNewImg({ ...newImg, caption: e.target.value })}
                placeholder="Caption — leave a blank line between paragraphs"
                rows={4}
                data-testid="admin-new-image-caption"
                className="border border-ink/20 px-4 py-2.5 focus:outline-none focus:border-windsor resize-y"
              />
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  value={newImg.alt}
                  onChange={(e) => setNewImg({ ...newImg, alt: e.target.value })}
                  placeholder="Alt text (describe the image)"
                  data-testid="admin-new-image-alt"
                  className="border border-ink/20 px-4 py-2.5 focus:outline-none focus:border-windsor"
                />
                <input
                  type="date"
                  value={newImg.date}
                  onChange={(e) => setNewImg({ ...newImg, date: e.target.value })}
                  data-testid="admin-new-image-date"
                  className="border border-ink/20 px-4 py-2.5 focus:outline-none focus:border-windsor text-ink/80"
                />
              </div>
              <button type="submit" disabled={savingImg} data-testid="admin-add-image-btn" className="bg-[#C2410C] text-ivory py-3 font-medium hover:bg-windsor-dark transition-colors disabled:opacity-60">
                {savingImg ? "Uploading…" : "Add image"}
              </button>
            </div>
          </form>
        </div>

        {/* NEWSLETTER */}
        <div className="mt-12" data-testid="admin-newsletter">
          <h2 className="font-serif text-2xl text-ink">Newsletter signups ({signups.length})</h2>
          <div className="mt-4 bg-white border border-ink/10 divide-y divide-ink/10">
            {signups.length === 0 && <p className="p-4 text-ink/50 font-light">No signups yet.</p>}
            {signups.map((s) => (
              <div key={s.id} className="p-4 flex justify-between text-sm">
                <span className="text-ink">{s.email}</span>
                <span className="text-ink/40">{new Date(s.created_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
